"use client";

import type {
  MuxPlayerProps,
  MuxPlayerRefAttributes,
} from "@mux/mux-player-react";
import MuxPlayerReact from "@mux/mux-player-react/lazy";
import { usePostHog } from "posthog-js/react";
import React from "react";

import {
  mergePlayerRefs,
  useWayflyerControlBarRef,
} from "@/components/video-player/use-wayflyer-control-bar";
import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { cn } from "@/lib/utils";

// `loading` exists only on the lazy entry point, whose props type isn't exported
type MuxPlayerLazyProps = MuxPlayerProps & { loading?: "page" | "viewport" };

// Re-type the lazy player with the public props type — the inferred JSX type of
// the raw component exceeds tsgo's union complexity limit (TS2590).
const MuxPlayerLazy = MuxPlayerReact as unknown as (
  props: Record<string, unknown>
) => React.JSX.Element;

export const MuxPlayer = React.forwardRef<
  MuxPlayerRefAttributes,
  MuxPlayerLazyProps
>(
  (
    {
      style = {},
      className,
      onPlay,
      autoPlay,
      loading = "viewport",
      preload = "none",
      ...props
    },
    ref
  ) => {
    const posthog = usePostHog();
    const wayflyerRef = useWayflyerControlBarRef();

    return (
      <MuxPlayerLazy
        ref={mergePlayerRefs(ref, wayflyerRef)}
        streamType="on-demand"
        {...props}
        autoPlay={autoPlay}
        loading={loading}
        preload={preload}
        disableCookies={true}
        accentColor="var(--pulse-700)"
        primaryColor="var(--grey-0)"
        secondaryColor="transparent"
        className={cn("wf-mux-player", className)}
        onPlay={(e: CustomEvent) => {
          posthog.capture("video_played", {
            title: props.title,
            id: props.playbackId,
          });
          onPlay?.(e);
        }}
        style={style}
      />
    );
  }
);

MuxPlayer.displayName = "MuxPlayer";

export function MuxPlayerPlayOnShow(
  props: Omit<React.ComponentProps<typeof MuxPlayer>, "autoPlay">
) {
  const ref = React.useRef(null);
  const inView = useIntersectedOnce(ref, {
    root: null,
    threshold: 0.7,
  });

  const { loading, ...playerProps } = props;

  return (
    <MuxPlayer
      {...playerProps}
      ref={ref}
      autoPlay={inView}
      loading={loading ?? "viewport"}
    />
  );
}
